// src/pages/contracts/components/ContractStatusFlow.js
import React from 'react';
import { CheckCircle, Circle, Clock, AlertCircle, FileText, Send, Eye, PenTool } from 'lucide-react';

const STATUS_MAP = {
  0: 'Draft',
  1: 'Generated',
  2: 'Sent',
  3: 'Accessed',
  4: 'Signed',
  5: 'Expired',
  6: 'Terminated'
};


const ContractStatusFlow = ({ contract, showLabels = true, size = 'default' }) => {
  const statuses = [
    { key: 'Draft', label: 'Ciornă', icon: FileText },
    { key: 'Generated', label: 'Generat', icon: FileText },
    { key: 'Sent', label: 'Trimis', icon: Send },
    { key: 'Accessed', label: 'Accesat', icon: Eye },
    { key: 'Signed', label: 'Semnat', icon: PenTool }
  ];

  const currentStatus =
    typeof contract.status === 'number'
      ? STATUS_MAP[contract.status]
      : contract.status;
  console.log('Current Status:', contract.status, '->', currentStatus);

  const getCurrentStatusIndex = () => {
    if (currentStatus === 'Expired' || currentStatus === 'Terminated') {
      return -1; // Special handling for terminal states
    }
    return statuses.findIndex(status => status.key === currentStatus);
  };

  const currentIndex = getCurrentStatusIndex();
  const isTerminalState = currentStatus === 'Expired' || currentStatus === 'Terminated';

  const getStepStatus = (index) => {
    if (isTerminalState) {
      return index <= currentIndex ? 'completed' : 'pending';
    }
    
    if (index < currentIndex) return 'completed';
    if (index === currentIndex) return 'completed';
    return 'pending';
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100 border-green-300';
      case 'current': return 'text-primary-600 bg-primary-100 border-primary-300';
      case 'pending': return 'text-gray-400 bg-gray-100 border-gray-300';
      default: return 'text-gray-400 bg-gray-100 border-gray-300';
    }
  };

  const getConnectorColor = (index) => {
    const status = getStepStatus(index);
    if (status === 'completed') return 'bg-green-400';
    if (status === 'current') return 'bg-primary-400';
    return 'bg-gray-300';
  };

  const sizeClasses = {
    small: {
      icon: 'w-6 h-6',
      text: 'text-xs',
      spacing: 'space-y-2'
    },
    default: {
      icon: 'w-8 h-8',
      text: 'text-sm',
      spacing: 'space-y-3'
    },
    large: {
      icon: 'w-10 h-10',
      text: 'text-base',
      spacing: 'space-y-4'
    }
  };

  const classes = sizeClasses[size];

  if (isTerminalState) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="flex items-center space-x-3">
          <div className={`rounded-full p-2 ${contract.status === 'Expired' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'}`}>
            <AlertCircle className={classes.icon} />
          </div>
          <div>
            <p className={`font-medium ${classes.text}`}>
              {contract.status === 'Expired' ? 'Contract Expirat' : 'Contract Reziliat'}
            </p>
            <p className="text-xs text-gray-500">
              {contract.status === 'Expired' 
                ? `Expirat pe ${new Date(contract.dueDate).toLocaleDateString('ro-RO')}`
                : 'Contract întrerupt'
              }
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Horizontal flow for larger screens */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between">
          {statuses.map((status, index) => {
            const stepStatus = getStepStatus(index);
            const IconComponent = status.icon;
            
            return (
              <React.Fragment key={status.key}>
                <div className="flex flex-col items-center">
                  {/* Icon */}
                  <div className={`
                    flex items-center justify-center rounded-full border-2 ${classes.icon}
                    ${getStepColor(stepStatus)}
                    transition-colors duration-200
                  `}>
                    {stepStatus === 'completed' ? (
                      <CheckCircle className="w-full h-full p-1" />
                    ) : stepStatus === 'current' ? (
                      <Clock className="w-full h-full p-1" />
                    ) : (
                      <IconComponent className="w-full h-full p-1" />
                    )}
                  </div>
                  
                  {/* Label */}
                  {showLabels && (
                    <div className="mt-2 text-center">
                      <p className={`font-medium ${classes.text} ${
                        stepStatus === 'completed' ? 'text-green-600' :
                        stepStatus === 'current' ? 'text-primary-600' :
                        'text-gray-400'
                      }`}>
                        {status.label}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Connector */}
                {index < statuses.length - 1 && (
                  <div className="flex-1 h-0.5 mx-4 bg-gray-300 relative">
                    <div 
                      className={`h-full transition-all duration-300 ${getConnectorColor(index)}`}
                      style={{ 
                        width: stepStatus === 'completed' ? '100%' : 
                               stepStatus === 'current' ? '50%' : '0%' 
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Vertical flow for mobile */}
      <div className="md:hidden">
        <div className={`flex flex-col ${classes.spacing}`}>
          {statuses.map((status, index) => {
            const stepStatus = getStepStatus(index);
            const IconComponent = status.icon;
            
            return (
              <React.Fragment key={status.key}>
                <div className="flex items-center">
                  {/* Icon */}
                  <div className={`
                    flex items-center justify-center rounded-full border-2 ${classes.icon}
                    ${getStepColor(stepStatus)}
                    transition-colors duration-200
                  `}>
                    {stepStatus === 'completed' ? (
                      <CheckCircle className="w-full h-full p-1" />
                    ) : stepStatus === 'current' ? (
                      <Clock className="w-full h-full p-1" />
                    ) : (
                      <IconComponent className="w-full h-full p-1" />
                    )}
                  </div>
                  
                  {/* Label */}
                  {showLabels && (
                    <div className="ml-4">
                      <p className={`font-medium ${classes.text} ${
                        stepStatus === 'completed' ? 'text-green-600' :
                        stepStatus === 'current' ? 'text-primary-600' :
                        'text-gray-400'
                      }`}>
                        {status.label}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Connector */}
                {index < statuses.length - 1 && (
                  <div className="flex justify-start ml-4">
                    <div className="w-0.5 h-6 bg-gray-300 relative">
                      <div 
                        className={`w-full transition-all duration-300 ${getConnectorColor(index)}`}
                        style={{ 
                          height: stepStatus === 'completed' ? '100%' : 
                                  stepStatus === 'current' ? '50%' : '0%' 
                        }}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Additional info */}
      {contract.hasBeenAccessed && currentIndex >= 2 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            ✓ Contractul a fost accesat de client
          </p>
        </div>
      )}
      
      {contract.daysUntilExpiry <= 30 && contract.daysUntilExpiry > 0 && !isTerminalState && (
        <div className="mt-4 p-3 bg-orange-50 rounded-lg">
          <p className="text-sm text-orange-700">
            ⚠️ Contractul expiră în {contract.daysUntilExpiry} zile
          </p>
        </div>
      )}
    </div>
  );
};

export default ContractStatusFlow;